import { FC, ReactElement, useRef, useState, useMemo, useContext } from 'react'
import styled from 'styled-components'
import List from 'components/List'
import { initializeApp } from 'firebase/app'
import { BiSlider } from 'react-icons/bi'
import { VscClose } from 'react-icons/vsc'
import { AiOutlineSearch } from 'react-icons/ai'
import Featured from 'components/Featured'
import Filters from 'components/Filters'
import { device } from '../../breakpoints'
import Agent from '../Agent'
import AppearingElement from '../AppearingElement'
import { TextWidth } from '@ta-interaktiv/react-disco-grid'
import { useDebouncedCallback } from 'use-debounce'
import { TrackingContext } from 'contexts'

const firebaseConfig = {
  apiKey: 'AIzaSyBGF-_kiDVID2D9_8A8i4zLtHWYxG704yA',
  authDomain: 'wch-streaming-v2.firebaseio.com',
  databaseURL: 'https://wch-streaming-v2.europe-west1.firebasedatabase.app/',
  storageBucket: 'wch-interactive.appspot.com',
  projectId: 'wch-interactive',
}

initializeApp(firebaseConfig)

interface ListProps {
  data: DataItem[]
  text?: string
}

interface DataItem {
  id: string
  title: string
  titleHeader: string
  lead: string
  updatedAt: Date
  highlighted: boolean
  favorite: boolean
  votes: {
    count: number
    value: number
  }
  note: number
  [key: string]: any // Allows for additional properties
}

interface FilterData {
  type: string
  provider: string
  liked: boolean
}

const ExampleComponent: FC<ListProps> = ({ data, text }): ReactElement => {
  const trackEvent = useContext(TrackingContext)
  const [textSearch, setTextSearch] = useState<string | null>(null)
  const [filters, setFilters] = useState<any>(new Map())
  const [showFilters, setShowFilters] = useState<boolean>(false)
  const [AISearchMode, setAISearchMode] = useState<boolean>(true)
  const [textValue, setTextValue] = useState<string>('')
  const containerRef = useRef<HTMLDivElement>(null)

  // Memoized all items array
  const items = useMemo(
    () =>
      data.map((item) => ({
        ...item,
        year: new Date(item.customUpdatedAt).getFullYear(),
        month: new Date(item.customUpdatedAt).getMonth(),
      })).sort((a, b) => {
        return b.updatedAt.getTime() - a.updatedAt.getTime()
      }),
    [data]
  )

  // Memoized featured items
  const featuredItems = useMemo(
    () => items.filter((item) => item.highlighted).slice(0, 2),
    [items]
  )

  // Memoized list items
  const listItems = useMemo(
    () =>
      items.filter((item) => !featuredItems.some((fi) => fi.id === item.id)),
    [items, featuredItems]
  )

  // Flatten filters
  const flattenFilters = useMemo(
    () => new Set([...filters.values()].reduce((a, b) => [...b, ...a], [])),
    [filters]
  )

  // Filtered items
  const filteredItems = useMemo(() => {
    if (flattenFilters.size === 0) return items

    return items.filter((item) => {
      const itemFilterData: FilterData = {
        type: item.titleHeader.split(' sur ')[0],
        provider: item.titleHeader.split(' sur ')[1],
        liked: item['favorite'] === true,
      }

      return [...filters.keys()]
        .filter(
          (key) =>
            !['publicCount', 'publicRating', 'note', 'sort'].includes(key)
        )
        .every((filterType) => {
          const filterItems = filters.get(filterType)
          if (!filterItems || filterItems.size === 0) return true
          let isInFilters = filterItems.has(
            itemFilterData[filterType as keyof FilterData]
          )
          if (filterItems.has('liked')) {
            isInFilters =
              filterItems.size === 1
                ? itemFilterData.liked
                : isInFilters && itemFilterData.liked
          }
          return isInFilters
        })
    })
  }, [items, filters, flattenFilters])

  // Text search
  const outputItems = useMemo(() => {
    if (!textSearch) return filteredItems
    const normalizedSearch = textSearch
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
    return filteredItems.filter((item) =>
      (item.title + ' ' + item.titleHeader + ' ' + item.lead)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .includes(normalizedSearch)
    )
  }, [filteredItems, textSearch])

  // Sort items
  const sortedOutputItems = useMemo(() => {
    if (!filters.has('sort')) {
      return outputItems.sort((a, b) => b.year - a.year || b.month - a.month)
    }

    const sortOption = filters.get('sort')
    switch (sortOption) {
      case 'publicCount':
        return [...outputItems].sort((a, b) => b.votes.count - a.votes.count)
      case 'publicRating':
        return [...outputItems].sort((a, b) => b.votes.value - a.votes.value)
      case 'note':
        return [...outputItems].sort((a, b) => b.note - a.note)
      default:
        return outputItems
    }
  }, [outputItems, filters])

  const debounced = useDebouncedCallback((value) => {
    // Track the search event
    trackEvent &&
      trackEvent('search', {
        search_keyword: value,
        search_type: 'streaming',
        ai_enabled: false,
        search_results: sortedOutputItems.length,
        suggested_input: false,
      })
  }, 2000)

  const resetFilters = () => {
    setShowFilters(false)
    setFilters(new Map())
    setTextSearch(null)
    setTextValue('')
  }

  return (
    <Container ref={containerRef}>
      <TextWidth>
        <>
          {/* Animate the Regular search box when it appears or disappears */}
          <AppearingElement maxHeight={90} show={!AISearchMode}>
            <RegularSearchContainer>
              <TextSearchContainer>
                <TextSearch
                  value={textValue}
                  onKeyDown={(e) => {
                    e.key === 'Enter' &&
                      (e.target as HTMLTextAreaElement).blur()
                  }}
                  placeholder='Que recherchez-vous?'
                  type='text'
                  onChange={(e) => {
                    const val = e.target.value
                    setTextValue(val)
                    if (val.length > 2) {
                      setTextSearch(val)
                      debounced(val)
                    } else {
                      setTextSearch(null)
                    }
                  }}
                />
                {textValue.length > 0 && (
                  <ResetIcon
                    onClick={() => {
                      setTextValue('')
                      setTextSearch(null)
                    }}
                  >
                    <VscClose />
                  </ResetIcon>
                )}
                <SearchIcon>
                  <AiOutlineSearch />
                </SearchIcon>
              </TextSearchContainer>
              <FiltersButton
                onClick={() => {
                  setShowFilters(!showFilters)
                }}
              >
                <BiSlider /> <FilterText>Filtres</FilterText>
              </FiltersButton>
            </RegularSearchContainer>
          </AppearingElement>

          {/* Animate the AI box when it appears or disappears */}
          <AppearingElement maxHeight={600} show={AISearchMode}>
            <Agent
              handleHideAgent={(hasMessages: boolean) => {
                setAISearchMode(false)
                console.log('Hide agent. Messages:', hasMessages)
                hasMessages &&
                  containerRef.current?.scrollIntoView({ behavior: 'smooth' })
              }}
            />
          </AppearingElement>

          {/* Filters box */}
          {showFilters && (
            <FiltersContainer>
              <Filters
                data={data}
                handleFiltersChange={(filters: any) => setFilters(filters)}
              />
            </FiltersContainer>
          )}
        </>
      </TextWidth>

      {/* Results text */}
      <TextWidth>
        <FiltersResult>
          {(flattenFilters.size > 0 || textSearch !== null) &&
            (sortedOutputItems.length > 0 ? (
              <>
                <strong>{`${sortedOutputItems.length} fiche${
                  sortedOutputItems.length > 1 ? 's' : ''
                }`}</strong>
                &nbsp;correspond{sortedOutputItems.length > 1 ? 'ent' : ''} à
                vos critères.
              </>
            ) : (
              sortedOutputItems.length === 0 && `Aucun résultat`
            ))}
        </FiltersResult>
      </TextWidth>

      {/* AI Activation box */}
      <TextWidth style={{marginBottom: !text ? 40 : 0}}>
        <AppearingElement maxHeight={150} show={!AISearchMode}>
          <AIActivationBox>
            <ToggleBox>
              <ToggleOption
                onClick={() => {
                  setAISearchMode(AISearchMode)
                  resetFilters()
                }}
                active={!AISearchMode}
              >
                Recherche classique
              </ToggleOption>
              <ToggleOption
                onClick={() => {
                  setAISearchMode(!AISearchMode)
                  resetFilters()
                }}
                active={AISearchMode}
              >
                Recherche IA
              </ToggleOption>
            </ToggleBox>
          </AIActivationBox>
        </AppearingElement>
      </TextWidth>

      {text && AISearchMode && (
        <TextWidth style={{ margin: '2em 0' }}>{text}</TextWidth>
      )}

      {/* Results list */}
      <ContentWidth>
        <Content>
          <>
            {filters.size === 0 && textSearch === null && (
              <Featured data={featuredItems} />
            )}
          </>
          <List
            paginate={filters.size === 0 && textSearch === null}
            groupByMonth={filters.size === 0 && textSearch === null}
            data={
              filters.size === 0 && textSearch === null
                ? listItems
                : sortedOutputItems
            }
          />
        </Content>
      </ContentWidth>
    </Container>
  )
}
const Container = styled.div`
  font-family: var(--font-noto);
  font-size: 1.8rem;
  line-height: 3rem;
  margin-top: 40px;
`

const Content = styled.div`
  padding: 0 15px;
`

const Notes = styled.div`
  font-size: 1.35rem;
  line-height: 1;
  display: block;
  white-space: nowrap;
  margin-top: 1rem;

  a,
  a:visited,
  a:active {
    color: ${({ theme }) => theme.primaryButtonText};
  }
`

const BoxTitle = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
`

const BoxContent = styled.div`
  display: flex;
  flex-direction: row;
`

const BoxIcon = styled.div`
  font-size: 3.8rem;
`

const BoxText = styled.div`
  margin-left: 1.2rem;
  width: 80%;
`

const FiltersContainer = styled.div`
  background-color: ${({ theme }) => theme.colorGreyLight};
  padding: 2rem;
`

const AIActivationBox = styled.div`
  padding: 2rem 0;
  padding-top: 1rem;
  font-size: 1.6rem;
  font-family: var(--font-plex);
  -webkit-font-smoothing: antialiased;
  // background-color: ${({ theme }) => theme.primaryButtonBackground};
  // color: ${({ theme }) => theme.primaryButtonText};
  display: flex;
  justify-content: center;
`

const ToggleBox = styled.div`
  display: flex;
  justify-content: center;
  font-size: 0.85em;
`

const ToggleOption = styled.div<{ active: boolean }>`
  padding: 0.35em 0.5em;
  cursor: pointer;
  font-weight: ${({ active }) => (active ? 'bold' : 'normal')};
  background-color: ${({ theme, active }) =>
    active ? `${theme.colorText}` : 'transparent'};
  color: ${({ theme, active }) =>
    active ? 'var(--site-background)' : `${theme.colorText}`};
  border-radius: 1rem;
  border: 1px solid ${({ theme }) => theme.colorGrey};
  line-height: 1.4;

  &:hover {
    background-color: ${({ theme, active }) =>
      !active && `${theme.colorGrey}77`};
  }

  &:first-child {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-right: 0;
  }

  &:last-child {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
`

const RegularSearchContainer = styled.div`
  display: flex;
  margin: 0;
  background-color: ${({ theme }) => theme.colorGreyLight};
  padding: 2rem;

  @media ${device.tablet}, @media ${device.laptop} {
    margin: 0;
  }
`

const TextSearch = styled.input`
  border-radius: 0;
  padding: 15px;
  border: 0;
  width: 100%;
  box-sizing: border-box;
  background-color: var(--site-background);
  outline: none;
  -webkit-appearance: none;
  font-size: 1.6rem;
  font-family: var(--font-plex);
  color: ${({ theme }) => theme.colorText};
  margin: 0;
`

const TextSearchContainer = styled.div`
  font-family: var(--font-plex);
  font-weight: 700;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  border-bottom: 2px solid ${({ theme }) => theme.colorText};
  background-color: #eaeaea;
  flex: 1;
`

const SearchIcon = styled.div`
  font-size: 2.25rem;
  color: var(--brandblue-main);
  display: flex;
  align-items: center;
  padding: 0 15px;
  cursor: pointer;
`

const ResetIcon = styled.div`
  font-size: 2.25rem;
  color: var(--brandblue-main);
  display: flex;
  align-items: center;
  padding: 0 15px;
  cursor: pointer;
`

const FiltersButton = styled.div`
  background-color: ${({ theme }) => theme.primaryButtonBackground};
  color: ${({ theme }) => theme.primaryButtonText};
  font-family: var(--font-plex);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
  white-space: nowrap;
  cursor: pointer;
  line-height: 1;
  margin-left: 15px;
  font-size: 1.5rem;
  -webkit-font-smoothing: antialiased;
  font-weight: 700;
  transition: opacity 0.2s ease-in-out;
  letter-spacing: 0.02rem;

  &:hover {
    opacity: 0.85;
  }

  &:hover {
    opacity: 0.85;
  }
`

const FilterText = styled.div`
  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    display: none;
    margin-left: 0;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    display: block;
    margin-left: 10px;
  }
`

const FiltersResult = styled.div`
  font-family: var(--font-plex);
  font-size: 1.45rem;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0;
  width: 100%;
  font-weight: 300;
  color: ${({ theme }) => theme.colorText};
  background-color: ${({ theme }) => theme.colorGreyLight};
`

const ContentWidth = styled.div`
  margin: 0 auto;
  max-width: 1072px;
`

export default ExampleComponent
